import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Search, FileText, Map, Bike, Clock, Calendar } from 'lucide-react'
import { useLanguageStore, useCurrencyStore } from '../../lib/store'
import { formatPrice } from '../../lib/utils'
import { SearchBar } from '../../components/ui/SearchBar'

interface SearchResult {
    id: string | number
    type: 'article' | 'tour' | 'vehicle'
    title_vi?: string
    title_en?: string
    excerpt_vi?: string
    excerpt_en?: string
    description_vi?: string
    description_en?: string
    featured_image?: string
    featuredImage?: string
    url: string
    price?: number
    dailyRate?: number
    duration?: string
    author?: string
    created_at?: string
}

type FilterType = 'all' | 'article' | 'tour' | 'vehicle'

export default function SearchResultsPage() {
    const { query } = useParams<{ query: string }>()
    const { language } = useLanguageStore()
    const { currency, exchangeRate } = useCurrencyStore()

    const [results, setResults] = useState<SearchResult[]>([])
    const [loading, setLoading] = useState(true)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [filter, setFilter] = useState<FilterType>('all')

    const decodedQuery = decodeURIComponent(query || '')

    // Fetch results
    useEffect(() => {
        if (!decodedQuery) return

        const fetchResults = async () => {
            setLoading(true)
            try {
                const typeParam = filter === 'all' ? '' : `&type=${filter}`
                const res = await fetch(`/api/search?q=${encodeURIComponent(decodedQuery)}&page=${page}&limit=12${typeParam}`)
                const data = await res.json()
                if (data.success) {
                    setResults(data.results || [])
                    setTotal(data.total || 0)
                    setTotalPages(data.totalPages || 0)
                }
            } catch (error) {
                console.error('Search error:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchResults()
    }, [decodedQuery, page, filter])

    // Reset page when filter changes
    useEffect(() => {
        setPage(1)
    }, [filter])

    const getTitle = (item: SearchResult) => language === 'en' ? item.title_en : (item.title_vi || item.title_en)
    const getExcerpt = (item: SearchResult) => {
        const excerpt = language === 'en'
            ? (item.excerpt_en || item.description_en)
            : (item.excerpt_vi || item.description_vi || item.excerpt_en || item.description_en)
        return excerpt || ''
    }
    const getImage = (item: SearchResult) => item.featured_image || item.featuredImage

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'article': return <FileText className="w-4 h-4" />
            case 'tour': return <Map className="w-4 h-4" />
            case 'vehicle': return <Bike className="w-4 h-4" />
            default: return null
        }
    }

    const filters: { key: FilterType; label: { vi: string; en: string } }[] = [
        { key: 'all', label: { vi: 'Tất cả', en: 'All' } },
        { key: 'article', label: { vi: 'Bài viết', en: 'Articles' } },
        { key: 'tour', label: { vi: 'Tour', en: 'Tours' } },
        { key: 'vehicle', label: { vi: 'Xe', en: 'Vehicles' } }
    ]

    return (
        <>
            <Helmet>
                <title>{language === 'en' ? `Search: ${decodedQuery}` : `Tìm kiếm: ${decodedQuery}`} | Phú Quốc</title>
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b">
                    <div className="container mx-auto px-4 py-6">
                        {/* Search Bar */}
                        <div className="max-w-2xl mx-auto mb-6">
                            <SearchBar
                                className="w-full"
                                inputClassName="bg-gray-50"
                            />
                        </div>

                        {/* Query & Results Count */}
                        <div className="text-center">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                                {language === 'en'
                                    ? `Results for "${decodedQuery}"`
                                    : `Kết quả cho "${decodedQuery}"`
                                }
                            </h1>
                            <p className="text-gray-600">
                                {language === 'en'
                                    ? `Found ${total} results`
                                    : `Tìm thấy ${total} kết quả`
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 py-6">
                    {/* Filters */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {filters.map(f => (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition ${filter === f.key
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border'
                                    }`}
                            >
                                {f.label[language]}
                            </button>
                        ))}
                    </div>

                    {/* Results */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                                    <div className="h-40 bg-gray-200 rounded-lg mb-3" />
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                                </div>
                            ))}
                        </div>
                    ) : results.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {results.map((item) => (
                                    <Link
                                        key={`${item.type}-${item.id}`}
                                        to={item.url}
                                        className="bg-white rounded-xl overflow-hidden border shadow-sm hover:shadow-lg transition group"
                                    >
                                        {/* Image */}
                                        <div className="h-40 bg-gray-100 overflow-hidden">
                                            {getImage(item) ? (
                                                <img
                                                    src={getImage(item)}
                                                    alt={getTitle(item) || ''}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                                    {getTypeIcon(item.type)}
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="p-4">
                                            {/* Type badge */}
                                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                                                {getTypeIcon(item.type)}
                                                <span className="capitalize">
                                                    {item.type === 'article' ? (language === 'en' ? 'Article' : 'Bài viết') :
                                                        item.type === 'tour' ? 'Tour' :
                                                            (language === 'en' ? 'Vehicle' : 'Xe')}
                                                </span>
                                            </div>

                                            {/* Title */}
                                            <h3 className="font-bold text-gray-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition">
                                                {getTitle(item)}
                                            </h3>

                                            {/* Excerpt */}
                                            {getExcerpt(item) && (
                                                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                                                    {getExcerpt(item)}
                                                </p>
                                            )}

                                            {/* Meta */}
                                            <div className="flex items-center justify-between text-sm">
                                                {item.type === 'tour' && item.price && (
                                                    <span className="font-bold text-primary-600">
                                                        {formatPrice(item.price, currency, exchangeRate)}
                                                    </span>
                                                )}
                                                {item.type === 'vehicle' && item.dailyRate && (
                                                    <span className="font-bold text-primary-600">
                                                        {formatPrice(item.dailyRate, currency, exchangeRate)}/
                                                        <span className="text-gray-400 font-normal text-xs">
                                                            {language === 'en' ? 'day' : 'ngày'}
                                                        </span>
                                                    </span>
                                                )}
                                                {item.type === 'article' && item.created_at && (
                                                    <span className="flex items-center gap-1 text-gray-400">
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(item.created_at).toLocaleDateString()}
                                                    </span>
                                                )}
                                                {item.duration && (
                                                    <span className="flex items-center gap-1 text-gray-400">
                                                        <Clock className="w-3 h-3" />
                                                        {item.duration}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-center items-center gap-2 mt-8">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="px-4 py-2 rounded-lg border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        {language === 'en' ? 'Previous' : 'Trước'}
                                    </button>
                                    <span className="px-4 py-2 text-sm">
                                        {page} / {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="px-4 py-2 rounded-lg border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        {language === 'en' ? 'Next' : 'Tiếp'}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-16">
                            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                {language === 'en' ? 'No results found' : 'Không tìm thấy kết quả'}
                            </h2>
                            <p className="text-gray-600 mb-6">
                                {language === 'en'
                                    ? 'Try different keywords or browse our categories'
                                    : 'Thử từ khóa khác hoặc duyệt danh mục của chúng tôi'
                                }
                            </p>
                            <div className="flex flex-wrap justify-center gap-3">
                                <Link to="/blog" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition">
                                    {language === 'en' ? 'Browse Blog' : 'Xem Blog'}
                                </Link>
                                <Link to="/tours" className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition">
                                    {language === 'en' ? 'View Tours' : 'Xem Tour'}
                                </Link>
                                <Link to="/vehicles" className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition">
                                    {language === 'en' ? 'Rent Vehicles' : 'Thuê Xe'}
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
